import React, { FC, ReactNode } from 'react';
import { Box, Divider, Grid, Theme, Typography } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import AccordionTextBlock, { AccordionTextBlockProps } from './AccordionTextBlock';

export interface TextWithImageBlockProps {
  title: string;
  description?: string;
  listItems?: Array<{ boldText?: string; text?: ReactNode }>;
  bgImage: string;
  hasListItems?: boolean;
  listAccordionItems?: Array<AccordionTextBlockProps> | null;
  id?: string;
  textTitle?: string;
  descriptionHTML?: string;
}

const useStyles = makeStyles((theme: Theme) => {
  return createStyles({
    box: {
      backgroundColor: theme.palette.common.white,
    },
    photoBox: {
      backgroundImage: ({ bgImage }: Partial<TextWithImageBlockProps>) => `url(${bgImage})`,
      minHeight: 289,
    },
    title: {
      fontWeight: 700,
      color: theme.palette.common.white,
      marginBottom: theme.spacing(3),
      maxWidth: '100%',
    },
    divider: {
      height: 3,
      maxWidth: 100,
      backgroundColor: theme.palette.common.white,
      margin: '0 auto',
    },
    textBox: {
      padding: theme.spacing(5, 3),
      backgroundColor: theme.palette.common.white,
      [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(5, 10),
      },
    },
    textBlock: {
      padding: theme.spacing(4),
      border: `1px solid ${theme.palette.primary.contrastText}`,
      color: theme.palette.text.secondary,
      lineHeight: 1.8,
      '& ul': {
        paddingLeft: theme.spacing(3),
        [theme.breakpoints.up('sm')]: {
          paddingLeft: theme.spacing(4),
        },
      },
    },
    text: {
      color: theme.palette.text.secondary,
      lineHeight: 1.8,
      '& a': {
        textDecoration: 'underline !important',
      },
    },
    liItem: {
      color: '#292C3D',
      fontSize: 15,
      lineHeight: 1.8,
      '& span': {
        color: theme.palette.text.secondary,
      },
    },
    textTitle: {
      fontWeight: 700,
      color: '#292C3D',
      fontSize: 20,
      marginBottom: 5,
    },
  });
});

const TextWithImageBlock: FC<TextWithImageBlockProps> = (props) => {
  const {
    title,
    id,
    descriptionHTML,
    description,
    listItems,
    textTitle,
    bgImage,
    hasListItems = false,
    listAccordionItems,
  } = props;
  const classes = useStyles({ bgImage });

  return (
    <Box id={id} pb={5} className={classes.box}>
      <Grid container direction="column" justify="center" className={classes.photoBox}>
        <Grid item>
          <Typography variant="h3" className={classes.title} align="center">
            {title}
          </Typography>
          <Divider className={classes.divider} />
        </Grid>
      </Grid>
      <Box className={classes.textBox}>
        {hasListItems ? (
          <Box className={classes.textBlock}>
            {textTitle && <Typography className={classes.textTitle}>{textTitle}</Typography>}
            {description && <Typography className={classes.text}>{description}</Typography>}
            {descriptionHTML && <Typography className={classes.text} dangerouslySetInnerHTML={{ __html: descriptionHTML }} />}
            {!!listItems?.length && (
              <ul>
                {listItems.map(({ text, boldText }, index) => (
                  <li className={classes.liItem} key={index}>
                    {boldText && <span style={{ fontWeight: 600 }}>{boldText}</span>} {text && <span>{text}</span>}
                  </li>
                ))}
              </ul>
            )}
          </Box>
        ) : (
          listAccordionItems.map(({ text, title, listItems, description, listBlocks, manyBlocks }, index) => (
            <AccordionTextBlock
              text={text}
              title={title}
              listItems={listItems}
              description={description}
              listBlocks={listBlocks}
              manyBlocks={manyBlocks}
              key={index}
            />
          ))
        )}
      </Box>
    </Box>
  );
};

export default TextWithImageBlock;
